import type { Document } from "@contentful/rich-text-types";
import { CommonCMS, commonReplacements } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";

import { FormControl } from "@ui/components/forms/form-control/FormControl";
import { FormErrorMessage } from "@ui/components/forms/form-control/FormError";
import { Form, Formik } from "formik";

import { renderRichTextToComponent } from "@components/cms/rich-text-renderer/renderRichTextToComponent";
import { Button } from "@ui/components/forms/button/Button";
import { Box } from "@ui/components/layout/box/Box";

import useCreateAccount from "@hooks/useCreateAccount";
import getCreateAccountValidationSchema from "@lib/validators/user/createAccountSchema";
import type { FC } from "react";
import { useState } from "react";
import { FormBuilder } from "./components/form-builder/FormBuilder";
import { getConfig } from "./components/form-builder/config";
import { activeSiteCountry } from "@config/site/site-config";
import { initialValuesBuilder } from "./utils/initial-values-builder/initialValuesBuilder";
import type { Values } from "./components/form-builder/types";

interface ISignUpFormProps {
    formId: string;
    policiesAgreementTerms: Document;
    onSuccessCreateAccount: (firstName?: string) => void;
    email?: string;
    firstName?: string;
    lastName?: string;
    withNames?: boolean;
}

const SignUpForm: FC<ISignUpFormProps> = ({
    formId,
    policiesAgreementTerms,
    onSuccessCreateAccount = () => {},
    email = "",
    withNames = false,
    firstName,
    lastName,
}) => {
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const { createAccount } = useCreateAccount();
    const { getMultiple, get } = useMicrocopy();

    const country = activeSiteCountry;

    async function submitHandler(values, actions) {
        const response = await createAccount(values);
        if (response.status !== 200) {
            setIsError(true);
            if (response.status === 409) {
                setErrorMessage(headers.errors.existingAccount);
                return;
            }
            if (response.status === 401) {
                setErrorMessage(headers.errors.existingAccountResetPassword);
                return;
            }
            setErrorMessage(headers.errors.generalError);
            return;
        }

        onSuccessCreateAccount(values.firstName);
        actions.setSubmitting(false);
    }

    const microcopies = (() => {
        const errorsList = CommonCMS.error;
        return getMultiple(CommonCMS.shared, [
            CommonCMS.email,
            CommonCMS.password,
            CommonCMS.passwordRules,
            CommonCMS.firstName,
            CommonCMS.lastName,
            CommonCMS.phoneticFirstName,
            CommonCMS.phoneticLastName,
            CommonCMS.dateOfBirth,
            CommonCMS.button.createAccount,
            errorsList.email,
            errorsList.dateOfBirth,
            errorsList.general.title,
            errorsList.requiredFieldError,
            errorsList.existingUserAccount,
            errorsList.existingAccountResetPassword,
            errorsList.passwordRules,
        ]);
    })();

    const headers = {
        createAccount: microcopies[CommonCMS.button.createAccount],
        errors: {
            email: microcopies[CommonCMS.error.email],
            dateOfBirth: microcopies[CommonCMS.error.dateOfBirth],
            generalError: microcopies[CommonCMS.error.general.title],
            existingAccount: microcopies[CommonCMS.error.existingUserAccount],
            existingAccountResetPassword: microcopies[CommonCMS.error.existingAccountResetPassword],
            requiredField: microcopies[CommonCMS.error.requiredFieldError],
            passwordRules: microcopies[CommonCMS.error.passwordRules],
            maxEmailCharacters: get(CommonCMS.shared, CommonCMS.error.maxCharacters, {
                replacements: commonReplacements.maxEmailCharacters,
            }),
            maxNameCharacters: get(CommonCMS.shared, CommonCMS.error.maxCharacters, {
                replacements: commonReplacements.maxNameCharacters,
            }),
        },
    };

    return (
        <Formik<Values>
            validationSchema={getCreateAccountValidationSchema(headers.errors, withNames, country)}
            initialValues={initialValuesBuilder({
                values: {
                    email,
                    firstName,
                    lastName,
                },
                config: {
                    withNames,
                    country,
                },
            })}
            onSubmit={submitHandler}
        >
            {({ submitForm, errors, isSubmitting, touched }) => {
                return (
                    <Form noValidate={true} id={formId} data-testid="SignupFieldsForm">
                        <FormBuilder
                            values={{
                                email,
                                firstName,
                                lastName,
                            }}
                            withNames={withNames}
                            config={getConfig(country)}
                            errors={errors}
                            microcopies={microcopies}
                            touched={touched}
                        />
                        <FormControl isInvalid={isError} my={4}>
                            <FormErrorMessage>{errorMessage}</FormErrorMessage>
                        </FormControl>
                        <Button
                            form={formId}
                            w={"100%"}
                            onClick={submitForm}
                            type="submit"
                            isLoading={isSubmitting}
                            data-testid="createAccountButton"
                        >
                            {headers.createAccount}
                        </Button>
                        <Box mt={4} fontSize={["desktopBodyTextTiny", "mobileBodyTextSmall"]}>
                            {renderRichTextToComponent(policiesAgreementTerms)}
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default SignUpForm;
